import React, { useState, useEffect, useCallback, useRef } from "react";
import { Select } from "antd";
import { getOrgList, getFacilitiesByOrgId } from "./educationApi";
import SelectAllMultiple from "./SelectAllMultiple";

const { Option } = Select;

// Last organisation/facility picked on any Employee Connect screen, so moving
// between tabs keeps the selection. In memory only: a reload or sign-out
// starts empty. Facilities are kept per mode (single vs multiple select).
const remembered = { orgId: undefined, single: undefined, multiple: undefined, owner: null };
const currentOwner = () => `${localStorage.getItem("user_id")}|${localStorage.getItem("client_id")}`;

// Shared Organization -> Facility selector used across nearly every screen
// in this module (Registers' 5 tabs, KPI, Survey, Manage Education's
// Monthly/Toolbox Education tabs) — every one of them calls the same
// globalStore.getAllOrganizations()/getFacilityListByOrgId(orgId) pair in
// the old app, confirmed by reading each screen's store directly.
const OrgFacilityFilter = ({
  orgId,
  onOrgChange,
  facilityId,
  onFacilityChange,
  facilityMode, // "multiple" or undefined (single)
  orgWidth = 200,
  facilityWidth = 220,
  onFacilitiesLoaded, // optional: receives the facility list for the selected org
}) => {
  const [orgs, setOrgs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const restoring = useRef(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getOrgList();
      setOrgs(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const modeKey = facilityMode === "multiple" ? "multiple" : "single";

  const handleOrgChange = useCallback(async (value) => {
    if (!restoring.current) {
      remembered.orgId = value;
      remembered.single = undefined;
      remembered.multiple = undefined;
    }
    setFacilities([]);
    if (onFacilitiesLoaded) onFacilitiesLoaded([]);
    onOrgChange(value);
    if (!value) return [];
    const { error, result } = await getFacilitiesByOrgId(value);
    const list = !error && Array.isArray(result?.result) ? result.result : [];
    setFacilities(list);
    if (onFacilitiesLoaded) onFacilitiesLoaded(list);
    return list;
  }, [onOrgChange, onFacilitiesLoaded]);

  const handleFacilityChange = useCallback((value) => {
    remembered[modeKey] = value;
    onFacilityChange(value);
  }, [onFacilityChange, modeKey]);

  // On first show, restore the last choice — only into an empty screen.
  useEffect(() => {
    if (remembered.owner !== currentOwner()) {
      Object.assign(remembered, { orgId: undefined, single: undefined, multiple: undefined, owner: currentOwner() });
    }
    if (orgId || !remembered.orgId) return;
    const saved = remembered[modeKey];
    restoring.current = true;
    handleOrgChange(remembered.orgId).then((list) => {
      restoring.current = false;
      const ids = new Set(list.map((f) => f.facility_id ?? f.id));
      if (modeKey === "multiple" && Array.isArray(saved)) {
        const keep = saved.filter((id) => ids.has(id));
        if (keep.length) onFacilityChange(keep);
      } else if (modeKey === "single" && saved !== undefined && ids.has(saved)) {
        onFacilityChange(saved);
      }
    });
    // Runs once per mount by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Group</div>
        <Select showSearch optionFilterProp="children" placeholder="Select group" style={{ width: orgWidth }} value={orgId} onChange={handleOrgChange} allowClear>
          {orgs.map((o) => <Option key={o.org_id ?? o.id} value={o.org_id ?? o.id}>{o.org_name ?? o.name}</Option>)}
        </Select>
      </div>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility</div>
        {facilityMode === "multiple" ? (
          <SelectAllMultiple
            placeholder="Select facility"
            style={{ width: facilityWidth }}
            value={facilityId}
            onChange={handleFacilityChange}
            disabled={!orgId}
            allowClear
            maxTagCount={2}
            options={facilities.map((f) => ({ value: f.facility_id ?? f.id, label: f.facility_name ?? f.name }))}
          />
        ) : (
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="Select facility"
            style={{ width: facilityWidth }}
            value={facilityId}
            onChange={handleFacilityChange}
            disabled={!orgId}
            allowClear
          >
            {facilities.map((f) => <Option key={f.facility_id ?? f.id} value={f.facility_id ?? f.id}>{f.facility_name ?? f.name}</Option>)}
          </Select>
        )}
      </div>
    </>
  );
};

export default OrgFacilityFilter;
